import './roster-actions.css';
import { readJsonPath } from '../predicate.js';
import { changedNothing } from '../kernel-noop.js';
import { defaultSaveFile, type SaveFile } from './save-file.js';
import { askWhyThenStart, impersonationUnavailableReason } from './impersonation-door.js';
import type {
  RosterAction,
  RosterActionField,
  RosterActionFieldOption,
  RosterActionFieldsFrom,
  RosterActionPreview,
  RosterActionSubmit,
} from './roster-action-types.js';

/**
 * CAOS-337/CAOS-743 — the shared RENDERER for an authored `RosterAction` (kernel/src/meta/
 * metatypes.ts): the piece that turns a declared "button that calls a control-plane write route"
 * into a real confirm dialog or a real form dialog, POSTs it, and reports the result — reused by
 * both {@link ../surfaces/roster-page.js renderRosterPage} (page-level `pageActions`, e.g. "Invite
 * user") and {@link ../surfaces/roster-detail-page.js renderRosterDetailPage} (record-level
 * `actions`, and a table's per-row `rowAction`). Nothing here mentions Setup or Users — any package
 * authoring a `rosterPage`/`rosterDetailPage` gets the same working buttons.
 *
 * Token substitution (the module's one piece of "magic", named in the schema doc): a `params` value
 * written as exactly `"{recordId}"` becomes `recordId`; `"{rowId}"` becomes `rowId` (when the caller
 * supplies one — a page-level action has none). Every other `params` value is sent verbatim.
 *
 * CAOS-142 — `"{groupHeading}"` becomes the heading of the group the action was drawn inside, for a
 * table rendered through `groupsAt`. It exists because a row is not always enough to say WHERE it
 * is: the Page Layouts screen groups a layout's fields by section, a layout verb addresses the
 * section by its label, and the label is the group's, not the row's. `deploy/validate.ts`'s
 * `ROW_TOKENS` must name every token added here, or the gate judges it as literal characters.
 *
 * CAOS-557 — `"{selectedId}"` is the same substitution as `"{rowId}"`, under a distinct name a
 * page author uses on a `bulkAction` (as opposed to a `rowAction`) so the two read differently in
 * source even though the mechanism is identical: {@link runRosterBulkAction}/
 * {@link renderRosterBulkActionButton} call the SAME single-item verb once per currently-selected
 * row, passing that row's id through the `rowId` slot on each call — there is no batch verb, and
 * none is invented here.
 */

export type Navigate = (href: string) => void;

export interface ActionFetchResponse {
  json(): Promise<unknown>;
}
export type ActionFetch = (path: string, init?: { method?: string; body?: unknown }) => Promise<ActionFetchResponse>;

export const defaultActionFetch: ActionFetch = (path, init) =>
  fetch(path, {
    credentials: 'same-origin',
    method: init?.method ?? 'GET',
    ...(init?.body !== undefined ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(init.body) } : {}),
  }) as unknown as Promise<ActionFetchResponse>;

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { code?: string; message?: string; details?: Record<string, unknown>; [key: string]: unknown };
}

/**
 * CAOS-1904 — the tokens {@link substitute} recognises, as a set, for the one caller that needs to
 * ask whether an authored value came from the ADDRESS or was written into the document.
 *
 * Declared here rather than at that call site so it cannot drift from the function below: a token
 * added there and not here would be treated as a literal and refused, which fails closed, and the
 * reverse — a token removed below and left here — is what a test pins.
 */
const ADDRESS_TOKENS: ReadonlySet<string> = new Set([
  '{recordId}',
  '{rowId}',
  '{selectedId}',
  '{groupHeading}',
]);

function substitute(
  value: string,
  recordId: string,
  rowId: string | undefined,
  groupHeading: string | undefined,
): string {
  if (value === '{recordId}') return recordId;
  if (value === '{rowId}') return rowId ?? '';
  if (value === '{selectedId}') return rowId ?? '';
  // CAOS-142 — the heading of the GROUP this action was drawn inside, for a table rendered through
  // `groupsAt`. Empty for an action anywhere else, the same way `{rowId}` is empty for a page-level
  // action: a token that cannot be filled here resolves to nothing and the route refuses the call,
  // rather than this inventing a value the screen never showed.
  if (value === '{groupHeading}') return groupHeading ?? '';
  return value;
}

/**
 * Resolve an action's fixed `params` against the current record id, plus the row id for a row
 * action and the group heading for a row action inside a grouped table.
 */
function resolveParams(
  action: RosterAction,
  recordId: string,
  rowId: string | undefined,
  groupHeading?: string,
  payload?: unknown,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(action.params ?? {})) out[k] = substitute(v, recordId, rowId, groupHeading);
  // CAOS-142 — `paramsFrom` reads the page's OWN payload, for a value the address cannot carry: the
  // Page Layouts screen is addressed by its object, and a layout verb names a layout by its key.
  //
  // A path that resolves to nothing becomes the empty string rather than being left out, so the
  // route refuses for a parameter it did not get instead of running with one fewer. Leaving it out
  // is the more dangerous reading: a verb whose optional parameter is simply absent does something
  // — usually something to a different record — and reports success.
  //
  // A path landing on an OBJECT or an ARRAY is treated the same way as one landing on nothing, and
  // deliberately: every route parameter is a scalar, so such a path is an author who named an
  // interior node rather than a value. `String()` would have posted "[object Object]" or a
  // comma-joined list as a layout key — a value nobody chose, of the exact kind the conflict refusal
  // exists to prevent, arriving by coercion instead of by precedence.
  for (const [k, path] of Object.entries(action.paramsFrom ?? {})) {
    const read = readJsonPath(payload, path);
    const scalar = typeof read === 'string' || typeof read === 'number' || typeof read === 'boolean';
    out[k] = scalar ? String(read) : '';
  }
  return out;
}

/** Whether an action is visible right now, per its optional `visibleWhen` against the current record. */
export function actionVisible(action: RosterAction, record: unknown): boolean {
  const w = action.visibleWhen;
  if (!w) return true;
  const actual = String(readJsonPath(record, w.valueAt) ?? '');
  const matches = actual === w.equals;
  return w.negate ? !matches : matches;
}

/**
 * CAOS-699 — whether a drawn action can be pressed right now, per its optional `enabledWhen`. The
 * same test as {@link actionVisible}; a failing one disables the button rather than removing it.
 */
export function actionEnabled(action: RosterAction, record: unknown): boolean {
  const w = action.enabledWhen;
  if (!w) return true;
  const matches = String(readJsonPath(record, w.valueAt) ?? '') === w.equals;
  return w.negate ? !matches : matches;
}

/**
 * CAOS-1904 — whose name the overlay's heading says, for an `impersonate` action.
 *
 * It is the PERSON'S name, never the action's. The button says "Log in as" and the heading says
 * "Act as Dana Whitfield", and reading the button's label into the heading would produce "Act as Log
 * in as" — which is the sort of thing that looks like a placeholder nobody filled in.
 *
 * `labelAt` is a path into the page's own payload, read through the same `readJsonPath` every
 * `valueAt` uses. It is authored because only the page knows where its person's name is: this
 * screen keeps it at `subject.displayName`, and nothing in the host could guess that.
 *
 * Falls back to the same words the account menu falls back to, so a page that declares nothing — or
 * whose path resolves to nothing, which is an empty display name rather than an error — still asks a
 * sensible question instead of "Act as undefined".
 */
function impersonationTargetLabel(
  action: Extract<RosterAction, { readonly kind: 'impersonate' }>,
  ctx: { readonly payload?: unknown; readonly record?: unknown },
): string {
  if (action.labelAt === undefined) return 'that person';
  const from = ctx.payload ?? ctx.record;
  const found = String(readJsonPath(from, action.labelAt) ?? '').trim();
  return found === '' ? 'that person' : found;
}

/**
 * CAOS-1837 — a file a route handed back, named and typed by the route itself.
 *
 * NOT AN AUTHORED PROPERTY, which is the whole design. A metatype field saying "this action
 * downloads" would have to be added to the kernel schema, re-emitted by the CLI, republished,
 * re-pinned and re-validated in every package repo before one author could use it — the wall
 * CAOS-1646's kvRows cascade hit — and it would also be a second place that can disagree with the
 * route about whether there is a file. The route already knows: `data export` returns the
 * filename, the media type and the bytes together, because an export's artifact and its manifest
 * are inseparable (`kernel/src/data/export.ts`). So the RESPONSE says there is a file, and any
 * route may say it. Nothing here names a verb.
 */
export interface ActionFile {
  readonly filename: string;
  readonly content: string;
  readonly contentType: string | undefined;
}

/**
 * Read a file off a successful response, or `null` when it carries none — which is every action
 * that existed before this one.
 *
 * Both `filename` and `content` are required and must be strings. An empty `content` IS a file: an
 * export of an object with no rows is a header and no lines, and refusing to save it would make
 * "nothing to export" indistinguishable from "the button is broken". An empty `filename` is not —
 * the browser would name the file after the page. A filename carrying a path separator is refused
 * rather than sanitized, for the same reason `RosterPageSchema.download` validates its own: a path
 * in a download name is not a naming choice, and quietly rewriting it hides whoever sent it.
 */
export function fileFromActionResponse(data: unknown): ActionFile | null {
  if (data == null || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d['filename'] !== 'string' || typeof d['content'] !== 'string') return null;
  const filename = d['filename'];
  if (!/^[A-Za-z0-9._-]+$/.test(filename)) return null;
  return {
    filename,
    content: d['content'],
    contentType: typeof d['contentType'] === 'string' ? d['contentType'] : undefined,
  };
}

/**
 * POST an action and, when the response carries a file, save it.
 *
 * `saveFile` is `null` for a press that cannot own a file. A bulk press is N calls with N
 * responses, so N files would land on the reader's disk from one click with nothing saying which
 * row each belongs to — the same ambiguity that made the schema refuse `reports` on a
 * `bulkAction`, and it is refused here on the same grounds rather than by picking one.
 */
async function postAction(
  doFetch: ActionFetch,
  action: RosterAction,
  body: Record<string, unknown>,
  saveFile: SaveFile | null = defaultSaveFile,
): Promise<Envelope> {
  const res = await doFetch(`/api/${action.verb.noun}/${action.verb.verb}`, { method: 'POST', body });
  const env = (await res.json()) as Envelope;
  if (env?.ok === true && saveFile) {
    const file = fileFromActionResponse(env.data);
    // Saved BEFORE the caller's own handling, so a dialog that dismisses on success cannot race
    // the file out of existence — and deliberately not treated as the whole outcome: `reports`
    // still draw, and a refusal still reports, because an export's row count and withheld-column
    // list are things only the response can say and the file does not state.
    if (file) saveFile(file.filename, file.content, file.contentType);
  }
  return env;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (className) e.className = className;
  return e;
}

/**
 * An action button, in the one shape every roster's header and row actions use.
 *
 * Exported for CAOS-551's export button, which is drawn into the same header strip and would
 * otherwise carry a second copy of these class names — the kind of duplicate that looks identical
 * on the day it is written and is a visibly different button a restyle later.
 */
export function button(label: string, style: 'primary' | 'default' | 'danger' = 'default'): HTMLButtonElement {
  const b = el('button', `roster-action-btn roster-action-btn--${style}`);
  b.type = 'button';
  b.textContent = label;
  return b;
}

/** A tiny modal shell (backdrop + panel), matching the style Setup's own hand-written dialogs use. */
function openDialog(titleText: string): { backdrop: HTMLElement; panel: HTMLElement; close: () => void } {
  const backdrop = el('div', 'roster-action-backdrop');
  const panel = el('div', 'roster-action-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', titleText);
  const title = el('h2', 'roster-action-title');
  title.textContent = titleText;
  panel.append(title);
  backdrop.append(panel);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close();
  });
  document.body.append(backdrop);
  function close(): void {
    backdrop.remove();
  }
  return { backdrop, panel, close };
}

/**
 * CAOS-1574 — a form whose field list is READ at runtime, and a write whose body is shaped from it.
 *
 * Kept as pure functions, taking a payload and returning a field list or a body, because they are
 * the part that decides what a person is shown and what the platform is asked to write. A DOM test
 * driving a dialog proves the wiring; these prove the decisions, and a field silently dropped or a
 * precondition silently absent is not a thing a wiring test would notice.
 */

/**
 * The platform's STORAGE vocabulary mapped onto the input kinds a roster form can draw.
 *
 * Deliberately partial. The thirteen storage types not named here are either computed (`Formula`,
 * `Rollup`, `AutoNumber` — a person editing one is a request the platform must refuse, not a
 * control to draw) or need a control this form does not have (`Reference`, `File`, `Geolocation`,
 * `Localizable`, `JSON`, `MultiSelect`, and the three remaining temporal kinds). Mapping one of
 * them onto a text box would take a typed value, hand it to somebody as a string, and write back
 * whatever they typed.
 *
 * So a field this form cannot draw is REPORTED rather than skipped — see
 * {@link fieldsFromPayload}. Dropping it quietly is the dangerous reading: the form would look
 * complete, submit without that value, and leave the person believing they had seen the record.
 */
export const STORAGE_INPUT: Readonly<Record<string, RosterActionField['type']>> = {
  Text: 'text',
  Number: 'number',
  Boolean: 'boolean',
  Date: 'date',
  Select: 'select',
};

/** A `format` modifier that changes which input a Text field is drawn as. */
const FORMAT_INPUT: Readonly<Record<string, RosterActionField['type']>> = {
  email: 'email',
  Email: 'email',
};

/** One field read out of a control-plane payload, and the reason it could not be drawn. */
export interface UndrawableField {
  readonly key: string;
  readonly storageType: string;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);
/**
 * A count, held to the same shape the AUTHORED field declares.
 *
 * Precision and scale reach the browser two ways — authored on the page, where the metatype says
 * `z.number().int().positive()`, and read from a payload at runtime, where nothing has said
 * anything. A negative or fractional scale makes a nonsense `step` that silently refuses every
 * value somebody types, so the runtime path applies the same rule rather than trusting the answer.
 */
const count = (v: unknown, floor: number): number | undefined =>
  typeof v === 'number' && Number.isInteger(v) && v >= floor ? v : undefined;

/**
 * Read a field list out of what `fieldsFrom.read` answered.
 *
 * Every part is addressed by an authored path, so the same mechanism serves configuration data,
 * Access Designer and field-level security rather than one payload shape. A row missing the two
 * paths a field cannot exist without — its key and its storage type — is not a field, and is
 * dropped; anything else missing has a defensible default (the label falls back to the key).
 */
export function fieldsFromPayload(
  spec: RosterActionFieldsFrom,
  payload: unknown,
): { fields: RosterActionField[]; undrawable: UndrawableField[] } {
  const raw = spec.fieldsAt === undefined ? payload : readJsonPath(payload, spec.fieldsAt);
  const fields: RosterActionField[] = [];
  const undrawable: UndrawableField[] = [];
  if (!Array.isArray(raw)) return { fields, undrawable };

  for (const row of raw) {
    if (row == null || typeof row !== 'object') continue;
    const key = str(readJsonPath(row, spec.keyAt));
    const storageType = str(readJsonPath(row, spec.storageTypeAt));
    if (key === undefined || storageType === undefined) continue;
    // A field the read names `__proto__` is not a field this form can submit: the value would
    // reassign the prototype of the object collecting it and never reach the body at all. Reported
    // rather than skipped, for the same reason every other undrawable one is — the person is told
    // which values this editor did not carry.
    if (!isBodyKey(key)) {
      undrawable.push({ key, storageType });
      continue;
    }

    const format = spec.formatAt === undefined ? undefined : str(readJsonPath(row, spec.formatAt));
    const base = STORAGE_INPUT[storageType];
    if (base === undefined) {
      undrawable.push({ key, storageType });
      continue;
    }
    const type = base === 'text' && format !== undefined ? (FORMAT_INPUT[format] ?? 'text') : base;
    const label = (spec.labelAt === undefined ? undefined : str(readJsonPath(row, spec.labelAt))) ?? key;
    const required = spec.requiredAt === undefined ? undefined : readJsonPath(row, spec.requiredAt) === true;
    const precision = spec.precisionAt === undefined ? undefined : count(readJsonPath(row, spec.precisionAt), 1);
    const scale = spec.scaleAt === undefined ? undefined : count(readJsonPath(row, spec.scaleAt), 0);
    const options = spec.valueSetAt === undefined ? undefined : valueSetOptions(readJsonPath(row, spec.valueSetAt));

    fields.push({
      key,
      label,
      type,
      ...(required === true ? { required } : {}),
      ...(precision === undefined ? {} : { precision }),
      ...(scale === undefined ? {} : { scale }),
      ...(options !== undefined && options.length > 0 ? { options } : {}),
    });
  }
  return { fields, undrawable };
}

/**
 * The option list a Select draws from, as a value set can answer it.
 *
 * Two shapes, because the control plane answers both: a plain array of values, and an array of
 * rows carrying a value and a label. A row with neither is not an option and is left out rather
 * than drawn as an empty one somebody could pick.
 */
function valueSetOptions(raw: unknown): RosterActionFieldOption[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: RosterActionFieldOption[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      if (item.length > 0) out.push({ value: item, label: item });
      continue;
    }
    if (item == null || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const value = str(row.value) ?? str(row.key);
    if (value === undefined) continue;
    out.push({ value, label: str(row.label) ?? value });
  }
  return out;
}

/**
 * Names that are not places in an object, and the assignment that respects that.
 *
 * `body['__proto__'] = x` does not add a key — it reassigns the object's prototype, and walking a
 * path through `__proto__` reaches `Object.prototype` and changes every object on the page. The
 * schema refuses to let one be AUTHORED, but a field key can also arrive from a control-plane read
 * at runtime, where no schema is between it and this code. So the rule is enforced here too, on
 * whichever of the two a name came from.
 */
const RESERVED_KEYS: ReadonlySet<string> = new Set(['__proto__', 'constructor', 'prototype']);
export const isBodyKey = (key: string): boolean => key.length > 0 && !RESERVED_KEYS.has(key);

/** Add `key` as an OWN property, never through a setter the prototype chain happens to carry. */
function setOwn(target: Record<string, unknown>, key: string, value: unknown): void {
  Object.defineProperty(target, key, { value, writable: true, enumerable: true, configurable: true });
}

/** Place `value` at a dot path inside `into`, creating the objects along the way. */
function placeAt(into: Record<string, unknown>, path: string, value: unknown): void {
  const segments = path.split('.');
  if (!segments.every(isBodyKey)) {
    throw new SubmitShapeError('This form is authored to write to a place that is not a field, so it will not save.');
  }
  let node = into;
  for (const segment of segments.slice(0, -1)) {
    const next = Object.prototype.hasOwnProperty.call(node, segment) ? node[segment] : undefined;
    // CAOS-1758 — an existing object at this segment is COPIED, not written through. It may be the
    // authored `envelope`'s own object: `{...submit.envelope}` is a shallow spread, so `body.component`
    // and `submit.envelope.component` were the same reference, and placing `component.key` wrote the
    // record's identity into the AUTHORED METADATA. A roster table hands one action object to every
    // row, so the next row's form then opened with the previous row's key already in its body — a
    // cross-record write arriving by a door the no-remapping rule does not watch. Copying here
    // keeps the merge behaviour (two bindings into one object both land) and ends the aliasing.
    const child =
      next != null && typeof next === 'object' && !Array.isArray(next) ? { ...(next as Record<string, unknown>) } : {};
    setOwn(node, segment, child);
    node = child;
  }
  setOwn(node, segments[segments.length - 1]!, value);
}

/** Raised when a write cannot be made the way it was authored, rather than made differently. */
export class SubmitShapeError extends Error {}

/** How many field keys a sentence names before it counts the remainder instead. */
const KEYS_NAMED = 6;

/**
 * The one place a list of field keys turns into prose, so the two sentences below cannot drift.
 *
 * Two things this does that a bare `.join(', ')` does not:
 *
 * It wraps each key in `"` and escapes it. These keys are read at runtime out of whatever `keyAt`
 * points at, so nothing constrains them to the identifier shape a hand-authored `fields` list has —
 * a key of `a, b` next to a key of `c` joins to `a, b, c`, which names three fields that do not
 * exist and hides the two that do. `JSON.stringify` does the wrapping rather than a hand-rolled
 * `"${key}"`, because a key containing a `"` defeats a hand-rolled one in exactly the same way a
 * comma defeats the bare join.
 *
 * It stops at {@link KEYS_NAMED}. A read naming two hundred undrawable fields is not far-fetched —
 * a describe of a wide object where every field is computed does it — and two hundred keys spliced
 * into one sentence is not a sentence anybody reads. The count that replaces them still carries the
 * fact that matters, which is that there are more.
 */
export function fieldKeyList(fields: readonly UndrawableField[]): string {
  const named = fields
    .slice(0, KEYS_NAMED)
    .map((f) => JSON.stringify(f.key))
    .join(', ');
  const rest = fields.length - KEYS_NAMED;
  return rest > 0 ? `${named}, and ${rest} more` : named;
}

/**
 * The note a drawn form carries about the fields it left out.
 *
 * Named, not swallowed. A field this form cannot draw is left out of the body too, so its value is
 * unchanged — but somebody who was not told would believe they had just reviewed the whole record.
 */
export function notEditableHereNote(undrawable: readonly UndrawableField[]): string {
  return `Not editable here: ${fieldKeyList(undrawable)}.`;
}

/**
 * What to say when a form has nothing to draw, which is two different things.
 *
 * "There is nothing here" is TRUE only when the read named no fields at all. When it named fields
 * this form cannot draw — every one of them computed, or a Reference, or a kind no control exists
 * for yet — saying nothing is here tells somebody the opposite of what happened: there is data,
 * and this editor cannot reach it. They would go looking for a set they had already found.
 *
 * `verb` is the word the surface uses for what it does ("edit" behind a button, "add" for the form
 * kept open under a table), so the two read naturally without holding two copies of the sentence.
 */
export function nothingToDrawMessage(undrawable: readonly UndrawableField[], verb: string): string {
  if (undrawable.length === 0) return `There is nothing to ${verb} here.`;
  return `This form cannot ${verb} any of these fields: ${fieldKeyList(undrawable)}.`;
}

/**
 * The request body a form posts, shaped as the action asked for it.
 *
 * With no `submit` this is the flat merge every roster form has always posted, unchanged — the
 * feature is additive and no form written before it changes meaning.
 *
 * With one, the values are placed inside an authored envelope and the write carries the version
 * precondition. That precondition is the reason this throws rather than posting: a write authored
 * as conditional, posted unconditionally because the version could not be found, is the one
 * failure mode worth refusing outright. It silently overwrites whatever somebody else saved while
 * this form was open, and it does so looking exactly like a write that worked.
 */
export function shapeSubmitBody(
  submit: RosterActionSubmit | undefined,
  params: Record<string, string>,
  values: Record<string, unknown>,
  record: unknown,
  /**
   * CAOS-1758 — the addresses a `bodyFrom` binding may forward. Optional so every existing caller
   * and test keeps its meaning; a binding naming an address this call was not given THROWS rather
   * than sending the field absent, for the same reason the missing precondition does.
   */
  addresses: { readonly recordId?: string; readonly rowId?: string } = {},
): Record<string, unknown> {
  if (submit === undefined) return { ...params, ...values };

  const body: Record<string, unknown> = { ...params, ...(submit.envelope ?? {}) };

  /* CAOS-1758 — the bindings are placed BEFORE the values and the precondition, so a binding can
   * build the structure those two then land inside. `component.key` and `component.body.values`
   * share the `component` object, and `placeAt` creates what is missing on the way down, so the
   * order only matters in that both must be able to reach the same node — which they can, because
   * `placeAt` reuses an existing plain object at a segment rather than replacing it.
   *
   * A binding that resolves to nothing THROWS. The alternative is posting the field absent, which
   * for `component.key` means `component-upsert` refuses the write as malformed — the right refusal
   * for the wrong reason, at run time, to whoever pressed save. The precondition above already
   * chose this trade for the same reason; this is the same choice for the same kind of value. */
  for (const [path, binding] of Object.entries(submit.bodyFrom ?? {})) {
    let value: unknown;
    if (binding.from === 'recordId') value = addresses.recordId;
    else if (binding.from === 'rowId') value = addresses.rowId;
    else value = binding.name === undefined ? undefined : readJsonPath(record, binding.name);

    if (value === undefined || value === null) {
      throw new SubmitShapeError(
        `This form could not read the ${binding.from === 'payload' ? 'value' : 'record'} it needs to save ` +
          'what you changed, so it will not save. Reload the page and try again.',
      );
    }
    placeAt(body, path, value);
  }
  // `Object.assign` writes THROUGH a setter the prototype chain carries; these do not. Every name
  // reaching this point is either authored, and refused by the schema if it is not a place, or
  // read at runtime and filtered by {@link fieldsFromPayload} — this is the third guard, on the
  // assignment itself, because it is the only one that holds whatever the other two missed.
  if (submit.valuesAt === undefined) {
    for (const [name, value] of Object.entries(values)) if (isBodyKey(name)) setOwn(body, name, value);
  } else {
    placeAt(body, submit.valuesAt, values);
  }

  if (submit.expectedVersionFrom !== undefined) {
    const version = readJsonPath(record, submit.expectedVersionFrom);
    if (version === undefined || version === null) {
      throw new SubmitShapeError(
        'This form could not read the version of the record it is editing, so it will not save — ' +
          'saving without it could overwrite a change somebody else made. Reload the page and try again.',
      );
    }
    const as = submit.expectedVersionAs ?? 'expectedVersion';
    if (!isBodyKey(as)) {
      throw new SubmitShapeError('This form is authored to send its precondition under a name that is not a key, so it will not save.');
    }
    setOwn(body, as, version);
  }
  return body;
}

/** Populate a `<select>`'s options, either static or fetched from `optionsFrom`. */
async function fillSelectOptions(select: HTMLSelectElement, field: RosterActionField, doFetch: ActionFetch): Promise<void> {
  const opts: { value: string; label: string }[] = [...(field.options ?? [])];
  if (field.optionsFrom) {
    try {
      const res = await doFetch(`/api/${field.optionsFrom.read.noun}/${field.optionsFrom.read.verb}`);
      const env = (await res.json()) as Envelope;
      if (env.ok === true) {
        const raw = field.optionsFrom.rowsAt ? readJsonPath(env.data, field.optionsFrom.rowsAt) : env.data;
        if (Array.isArray(raw)) {
          for (const row of raw) {
            if (row == null || typeof row !== 'object') continue;
            const value = String(readJsonPath(row, field.optionsFrom.valueAt) ?? '');
            const label = String(readJsonPath(row, field.optionsFrom.labelAt) ?? value);
            if (value) opts.push({ value, label });
          }
        }
      }
    } catch {
      // fail-soft — an empty/short option list is preferable to a crash.
    }
  }
  select.replaceChildren();
  if (!field.required) {
    const blank = el('option');
    blank.value = '';
    blank.textContent = '—';
    select.append(blank);
  }
  for (const o of opts) {
    const opt = el('option');
    opt.value = o.value;
    opt.textContent = o.label;
    select.append(opt);
  }
}

/**
 * CAOS-1574 — the `step` a Number field is entered under, from its scale.
 *
 * Scale is the count of decimal places the value is stored with, so a scale of 0 admits whole
 * numbers only and a scale of 2 admits hundredths. Without this the browser's default step is 1
 * and a rate of 4.25 cannot be typed into a field declared to hold exactly that.
 */
function stepForScale(scale: number | undefined): string {
  if (scale === undefined) return 'any';
  return scale <= 0 ? '1' : `0.${'0'.repeat(scale - 1)}1`;
}

/**
 * CAOS-1574 — the largest value a `(precision, scale)` field can hold, as a decimal STRING.
 *
 * Built out of digits rather than arithmetic, because the arithmetic is wrong. `10 ** 14 - 0.01`
 * is `99999999999999.98` in a float64, so a currency field declared `precision: 16, scale: 2` —
 * an ordinary shape — would have refused its own largest legal value, and refused it at the
 * keyboard where somebody would have no way to tell why. The digits are exact by construction.
 */
function numberBound(precision: number | undefined, scale: number | undefined): string | undefined {
  if (precision === undefined) return undefined;
  const whole = precision - (scale ?? 0);
  if (whole <= 0) return undefined;
  return scale !== undefined && scale > 0 ? `${'9'.repeat(whole)}.${'9'.repeat(scale)}` : '9'.repeat(whole);
}

function buildFieldInput(field: RosterActionField, record: unknown, doFetch: ActionFetch): { row: HTMLElement; read: () => unknown } {
  const row = el('label', 'roster-action-field');
  const labelText = el('span', 'roster-action-field-label');
  labelText.textContent = field.label;
  row.append(labelText);

  const rawDefault = field.defaultAt ? readJsonPath(record, field.defaultAt) : undefined;
  const defaultValue = field.defaultAt ? String(rawDefault ?? '') : '';

  if (field.type === 'select') {
    const select = el('select');
    select.required = field.required === true;
    row.append(select);
    void fillSelectOptions(select, field, doFetch).then(() => {
      if (defaultValue) select.value = defaultValue;
    });
    return { row, read: () => select.value };
  }

  // CAOS-1574 — a Boolean is a checkbox, and it reads back as a BOOLEAN rather than as the string
  // "true". A form that posted "false" would have the route store a non-empty string for a value
  // the person had just switched off.
  if (field.type === 'boolean') {
    const box = el('input');
    box.type = 'checkbox';
    box.checked = rawDefault === true;
    row.append(box);
    return { row, read: () => box.checked };
  }

  const input = el('input');
  input.type =
    field.type === 'email'
      ? 'email'
      : field.type === 'password'
        ? 'password'
        : field.type === 'number'
          ? 'number'
          : field.type === 'date'
            ? 'date'
            : 'text';
  input.required = field.required === true;
  if (field.placeholder) input.placeholder = field.placeholder;
  if (defaultValue) input.value = defaultValue;
  if (field.type === 'number') {
    // CAOS-1574 — the modifiers decide what a value MAY be, so they are applied where the value is
    // entered. Refusing 4.257 at the keyboard is the same judgement the field's own scale makes,
    // made where it can still be corrected instead of at the far end of a write.
    input.step = stepForScale(field.scale);
    const bound = numberBound(field.precision, field.scale);
    if (bound !== undefined) {
      input.max = bound;
      input.min = `-${bound}`;
    }
  }
  row.append(input);
  // A number reads back as a NUMBER; an empty one reads back as absent rather than as 0, which is
  // a value somebody chose. Everything else is the string the person typed, exactly as before.
  if (field.type === 'number') {
    return { row, read: () => (input.value === '' ? undefined : Number(input.value)) };
  }
  return { row, read: () => input.value };
}

/**
 * CAOS-1574 — the fields a form draws: the authored ones, then whatever the read answers.
 *
 * Authored first, because a set of fields every instance has plus whatever this one declares is
 * the ordinary case; a runtime field repeating an authored key is left out rather than drawn
 * twice under the same name, since the two would then post over one another.
 */
interface ResolvedFormFields {
  fields: RosterActionField[];
  undrawable: UndrawableField[];
  /** The read did not answer. Retrying is the remedy. */
  failed: boolean;
  /**
   * The read answered, and named nothing this form can draw.
   *
   * A different situation from `failed` and told apart deliberately, because the remedies differ
   * and telling somebody to retry a thing that worked is worse than saying nothing. A set with no
   * fields yet is the ordinary case; a `fieldsAt` naming the wrong place looks identical from here
   * and is not, so the wording claims neither.
   *
   * Either way there is no form: a submit button over no controls posts an empty body, and an
   * empty body is a write.
   */
  empty: boolean;
}

async function resolveFormFields(
  fields: readonly RosterActionField[] | undefined,
  fieldsFrom: RosterActionFieldsFrom | undefined,
  doFetch: ActionFetch,
): Promise<ResolvedFormFields> {
  const authored = [...(fields ?? [])];
  if (fieldsFrom === undefined) return { fields: authored, undrawable: [], failed: false, empty: false };
  try {
    const res = await doFetch(`/api/${fieldsFrom.read.noun}/${fieldsFrom.read.verb}`);
    const env = (await res.json()) as Envelope;
    if (env.ok !== true) return { fields: authored, undrawable: [], failed: true, empty: false };
    const read = fieldsFromPayload(fieldsFrom, env.data);
    const taken = new Set(authored.map((f) => f.key));
    const drawn = [...authored, ...read.fields.filter((f) => !taken.has(f.key))];
    return { fields: drawn, undrawable: read.undrawable, failed: false, empty: drawn.length === 0 };
  } catch {
    return { fields: authored, undrawable: [], failed: true, empty: false };
  }
}

export interface RunActionContext {
  readonly action: RosterAction;
  /** The record this action is declared against (page row, or the addressed detail record). */
  readonly record: unknown;
  /** This record's own id — substituted for every `"{recordId}"` token in `params`. */
  readonly recordId: string;
  /** A table ROW's own id, for a `rowAction` — substituted for every `"{rowId}"` token. Absent otherwise. */
  readonly rowId?: string;
  /**
   * CAOS-142 — the heading of the GROUP this action was drawn inside, substituted for every
   * `"{groupHeading}"` token. Absent for a page action, and for a flat table: only a table rendered
   * through `groupsAt` has groups, and only there can a row sit somewhere a row id cannot name.
   */
  readonly groupHeading?: string;
  /**
   * CAOS-142 — the PAGE'S payload, which `paramsFrom` reads its values out of.
   *
   * Distinct from `record` above, which for a row action is the ROW. A row cannot carry what the
   * page knows: on the Page Layouts screen the row is a field, and the layout's key is at
   * `objects.0.layoutKey` in the payload the page was drawn from.
   */
  readonly payload?: unknown;
  readonly fetch?: ActionFetch;
  /**
   * CAOS-1837 — how a file this action's response carries reaches the reader's disk. Absent ⇒ the
   * real browser download ({@link defaultSaveFile}), which is what every caller wants; supplied by
   * a test so it can measure the file rather than the browser, exactly as a roster's `download`
   * button is tested.
   */
  readonly saveFile?: SaveFile;
  /** Called after a successful write so the caller can refresh its own data. */
  readonly onSuccess: () => void;
  /**
   * CAOS-699 — where a refusal is shown when there is no dialog to show it in (a `confirm` or
   * `immediate` action). Absent ⇒ `window.alert`, as before.
   */
  readonly onError?: (message: string) => void;
  /** CAOS-337/CAOS-743 — disposition follow-up support (see the module doc on `followUpOnField`). */
  readonly onDispositionNeeded?: (objectKeys: readonly string[], resubmit: (dispositions: Record<string, unknown>) => Promise<void>) => void;
}

/**
 * Report one line under a dialog's fields (or as a toast-like banner for a confirm action).
 *
 * `cls` picks the voice. A failure is one thing; a write that was ACCEPTED and changed nothing
 * (CAOS-1712) is another, and painting the second in the danger colour would claim the call
 * failed when it did not. Both classes are cleared before either is added: they occupy the same
 * slot, and a stale error left sitting beside a fresh notice reads as two separate things having
 * happened on one submit.
 */
function showBanner(panel: HTMLElement, message: string, cls: 'roster-action-error' | 'roster-action-notice'): void {
  panel.querySelectorAll('.roster-action-error, .roster-action-notice').forEach((node) => node.remove());
  const line = el('div', cls);
  line.textContent = message;
  panel.append(line);
}

/** Report one failure line under a dialog's fields (or as a toast-like banner for a confirm action). */
function showError(panel: HTMLElement, message: string): void {
  showBanner(panel, message, 'roster-action-error');
}

/** CAOS-1712 — report that the write was accepted and changed nothing. Not a failure. */
function showNotice(panel: HTMLElement, message: string): void {
  showBanner(panel, message, 'roster-action-notice');
}

/** CAOS-74 — one value a write reported, resolved against its response. */
interface ReportedValue {
  readonly label: string;
  readonly value: string;
  readonly shownOnce: boolean;
}

/**
 * CAOS-74 — resolve an action's declared `reports` against the response it just got back.
 *
 * SCALARS ONLY, and a path that resolves to anything else is DROPPED rather than coerced. The
 * neighbouring `paramsFrom` reader coerces a non-scalar to `''` because it must send something;
 * this one must not, because the equivalent here is a row captioned "Preview link" with nothing
 * after it — which reads as a link that came back empty rather than as one that never came. An
 * absent path is the ordinary case and says so: `previewUrl` is omitted outright when the Site has
 * no hostname bound, and the right answer then is to show the values that DID come back.
 */
function readReports(action: RosterAction, data: unknown): ReportedValue[] {
  const out: ReportedValue[] = [];
  for (const report of action.reports ?? []) {
    const read = readJsonPath(data, report.valueAt);
    if (typeof read !== 'string' && typeof read !== 'number' && typeof read !== 'boolean') continue;
    const value = String(read);
    if (value.length === 0) continue;
    out.push({ label: report.label, value, shownOnce: report.shownOnce === true });
  }
  return out;
}

/**
 * The reported values, as a labelled block with a copy control on each.
 *
 * The value is drawn in a readonly text input rather than as text, which is what makes it
 * selectable with a keyboard and copyable without the clipboard permission — the copy BUTTON is
 * the convenience, not the mechanism. `navigator.clipboard` is absent in a non-secure context and
 * under test, so the button is only added where it exists; a missing button leaves a field the
 * person can still select, where a button that silently did nothing would not.
 */
function reportBlock(reported: readonly ReportedValue[]): HTMLElement {
  const wrap = el('div', 'roster-action-reports');
  for (const item of reported) {
    const row = el('div', 'roster-action-report');
    const label = el('label', 'roster-action-report-label');
    label.textContent = item.label;
    const field = document.createElement('input');
    field.type = 'text';
    field.className = 'roster-action-report-value';
    field.readOnly = true;
    field.value = item.value;
    label.append(field);
    row.append(label);
    if (item.shownOnce) {
      const once = el('p', 'roster-action-report-once');
      once.textContent = 'Copy this now — it cannot be shown again.';
      row.append(once);
    }
    // The reference is CAPTURED, not re-read in the handler: `navigator.clipboard` is absent in a
    // non-secure context and under test, and reading it twice invites the two reads disagreeing.
    const clipboard = navigator.clipboard;
    if (clipboard?.writeText) {
      const copy = button('Copy');
      copy.classList.add('roster-action-report-copy');
      copy.addEventListener('click', () => {
        void clipboard.writeText(item.value).then(
          () => {
            copy.textContent = 'Copied';
          },
          () => {
            // A refused clipboard is not a failed action. The field still holds the value.
            copy.textContent = 'Press to select';
            field.select();
          },
        );
      });
      row.append(copy);
    }
    wrap.append(row);
  }
  return wrap;
}

/**
 * CAOS-74 — show what the write reported inside the dialog that is already open, and SEAL it.
 *
 * ── WHY THE DIALOG IS SEALED AND NOT MERELY ADDED TO ──────────────────────────────────────────
 * Keeping the dialog up is what stops the value being destroyed by a dismissal; it also leaves a
 * live, still-populated form sitting under the thing it just produced. Pressing it again is an easy
 * mistake to make — a dialog that did not close is readable as one that did not work — and for a
 * show-once value the consequence is the defect this whole reading exists to prevent: the second
 * press mints a SECOND value, this function replaces the block, and the first URL is gone having
 * never been copied. The write succeeded both times, so nothing would report a loss.
 *
 * So every control already in the panel is disabled before the report is drawn, and exactly one way
 * out is added. The report's own field stays live because it is appended AFTER the sealing — a
 * disabled input cannot be selected, which would defeat the point of showing it.
 */
function showReports(panel: HTMLElement, reported: readonly ReportedValue[], close?: () => void): void {
  panel
    .querySelectorAll('.roster-action-error, .roster-action-notice, .roster-action-reports, .roster-action-done')
    .forEach((n) => n.remove());
  panel
    .querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'button, input, select, textarea',
    )
    .forEach((control) => {
      control.disabled = true;
    });
  panel.append(reportBlock(reported));
  // Never conditional: the sealing above has just disabled this panel's own Cancel, so a dialog
  // without this would be one the person cannot close. `close` is supplied by every panel-bearing
  // caller; the fallback is here so that a future one that forgets cannot strand somebody.
  const dismiss = close ?? ((): void => panel.closest('.roster-action-backdrop')?.remove());
  const done = button('Done', 'primary');
  done.classList.add('roster-action-done');
  done.addEventListener('click', dismiss);
  panel.append(done);
}

/**
 * CAOS-74 — and where there is no dialog, open one to hold it.
 *
 * An `immediate` action has no panel, and the fallback its neighbours use is `window.alert`. That
 * is wrong for this: an alert cannot be copied from on every browser and is gone the moment it is
 * dismissed, which for a show-once value is the same as never having shown it.
 */
function openReportDialog(title: string, reported: readonly ReportedValue[]): void {
  const { panel, close } = openDialog(title);
  panel.append(reportBlock(reported));
  const done = button('Done', 'primary');
  done.addEventListener('click', close);
  panel.append(done);
}

/**
 * CAOS-1715 — the reading of the kernel's no-op convention moved to `../kernel-noop.js` when the
 * INSTALL direction needed the same one. Its docblock carries the enumeration of the producers and
 * the reason the list is not stated as a count. Imported rather than copied: the kernel means one
 * thing by the flag, and two readers of it would drift.
 */

/**
 * Phrased HERE, not in the kernel, and deliberately not naming which state.
 *
 * CAOS-1516 settled that split for the sibling refusal: the kernel carries the facts and each
 * surface says its own piece, because the kernel cannot know whether its reader has a terminal.
 * What this surface knows is that a write was accepted and moved nothing — it does not know the
 * vocabulary of every verb that can no-op, and inventing one sentence per outcome here is how
 * generic roster code starts needing a release every time a route is added.
 *
 * Checked against every route in the list above, not only the one that was reported. A value-set
 * deploy that changed nothing means the set already held those values; a metadata deploy that
 * changed nothing means the metadata was already what was being sent; a re-install or a second
 * promotion means the same of each. All of them are "already in the state this asked for", which
 * is why the sentence holds: it describes what the FLAG means, not what any verb does.
 */
const NOTHING_CHANGED = 'Nothing was changed — the workspace was already in the state this asked for.';

/**
 * Build and wire ONE button for a {@link RosterAction}. Clicking it opens a confirm or a form
 * dialog (per `action.kind`), POSTs on submit, and calls `onSuccess` after a real 2xx-shaped
 * `{ok:true}` envelope. A `validation.request` failure naming `action.followUpOnField` hands off to
 * `onDispositionNeeded` instead of a bare error, when the caller supplied one.
 */
export function renderRosterActionButton(ctx: RunActionContext): HTMLButtonElement {
  const { action } = ctx;
  const doFetch = ctx.fetch ?? defaultActionFetch;
  const btn = button(action.label, action.style ?? 'default');
  // CAOS-1904 AC2 — an `impersonate` control is additionally gated on the SESSION, which no page
  // can declare. `enabledWhen` reads a path into the record, and no such path can answer "you are
  // already acting as somebody else". A review found that case drawn PRESSABLE: the overlay
  // opened, a reason had to be typed, and only the submitted refusal said it was never going to
  // work — which is not the "drawn and unavailable, saying which reason applies" AC2 asks for.
  //
  // Read through the door rather than from a runtime the renderer does not have, which is also
  // what keeps the gate and the thing it gates in one module.
  const sessionBar = action.kind === 'impersonate' ? impersonationUnavailableReason() : null;
  const enabled = actionEnabled(action, ctx.record) && sessionBar === null;
  btn.disabled = !enabled;
  // CAOS-1903 — a drawn, unpressable button says why. Set only while it IS disabled: the reason
  // describes a state the record is in, so carrying it on an enabled button would caption a
  // control with the explanation for the opposite of what the reader is looking at.
  //
  // `title` because that is where a reader looks and because nothing in this frame owns a tooltip
  // layer; the limitation is real and recorded on the ticket rather than hidden — a disabled
  // button is not focusable, so this is reachable by pointer and not by keyboard. Making it
  // keyboard-reachable means `aria-disabled` and swallowing the click, which changes how every
  // existing gated action behaves and is not this change.
  //
  // The session bar wins over an authored reason when both apply. A record that is deactivated AND
  // a session already running are both true, and the one the reader must act on is the one that
  // blocks every such control on every page rather than this one — reactivating the person would
  // not make the button work.
  if (!enabled && sessionBar !== null) btn.title = sessionBar;
  else if (!enabled && action.enabledWhen?.reason) btn.title = action.enabledWhen.reason;
  const report = (panel: HTMLElement | undefined, message: string): void => {
    if (panel) showError(panel, message);
    else if (ctx.onError) ctx.onError(message);
    else window.alert(message);
  };

  async function submit(
    values: Record<string, unknown>,
    panel?: HTMLElement,
    close?: () => void,
    // CAOS-1574 — names that belong at the TOP of the body whatever shape the form asked for.
    // `dispositions` is one: it answers the route's own refusal, so nesting it under the form's
    // `valuesAt` alongside the edited values would send the follow-up somewhere the route does
    // not read it, and the resubmit would be refused again for the reason it had just answered.
    top?: Record<string, unknown>,
  ): Promise<void> {
    const params = resolveParams(action, ctx.recordId, ctx.rowId, ctx.groupHeading, ctx.payload);
    let body: Record<string, unknown>;
    try {
      const shaped = shapeSubmitBody(
        action.kind === 'form' ? action.submit : undefined,
        params,
        values,
        ctx.record,
        { ...(ctx.recordId === undefined ? {} : { recordId: ctx.recordId }), ...(ctx.rowId === undefined ? {} : { rowId: ctx.rowId }) },
      );
      body = { ...shaped, ...(top ?? {}) };
    } catch (error) {
      report(panel, error instanceof SubmitShapeError ? error.message : 'That did not work.');
      return;
    }
    btn.disabled = true;
    try {
      // CAOS-1837 — the one press that can own a file: one action on one record, so a file the
      // response carries belongs to exactly this click.
      const env = await postAction(doFetch, action, body, ctx.saveFile ?? defaultSaveFile);
      if (env.ok === true) {
        // CAOS-1712 — the refresh still happens, because the call WAS accepted and the roster may
        // have moved for other reasons; for a stranded install (CAOS-1711) the refetched row is
        // the one that now carries the marker explaining why this did nothing. What no longer
        // happens is the dismissal: the dialog stays up holding the one sentence that tells the
        // two outcomes apart, and the person closes it themselves.
        if (changedNothing(env.data)) {
          // An `immediate` action, and a plain `window.confirm` one, submit with NO panel. Those
          // must not go through `report`: with an `onError` supplied it paints this in the
          // caller's failure banner (`roster-detail-page.ts` builds that as
          // `roster-action-error roster-page-error`), which is the one claim this whole change
          // exists to stop making. `onError` is the failure channel and a no-op is not a failure.
          // There is no notice channel on the context, and `window.alert` is what `report` itself
          // falls back to when a caller supplies no hook, so it is the neutral one already in use
          // here. Adding an `onNotice` hook nothing passes would be a dead parameter; giving the
          // detail page a notice banner is a separate surface decision, not this fix.
          if (panel) showNotice(panel, NOTHING_CHANGED);
          else window.alert(NOTHING_CHANGED);
          ctx.onSuccess();
          return;
        }
        // CAOS-74 — a write whose response carries something only IT can report keeps the surface
        // up, for the same reason the no-op above does: dismissing it is the one outcome that
        // destroys the thing the person pressed the button for. `sites/create-preview` returns a
        // private URL built from a token stored only as a digest, so a dialog that closed here
        // would leave a working link nothing can ever show again.
        //
        // `onSuccess` still runs, exactly as in the no-op case — the write happened and the surface
        // behind this should reflect it. Only the dismissal waits for the person.
        const reported = readReports(action, env.data);
        if (reported.length > 0) {
          if (panel) showReports(panel, reported, close);
          else openReportDialog(action.label, reported);
          ctx.onSuccess();
          return;
        }
        close?.();
        ctx.onSuccess();
        return;
      }
      const followField = action.followUpOnField;
      // CAOS-743 — the kernel sends a refusal's machine fields under `error.details`; only its code and
      // message sit beside them. Reading the top level found nothing, so the follow-up never opened
      // and a user who still owned records could not be deactivated from this screen at all.
      const followList = followField ? env.error?.details?.[followField] : undefined;
      if (followField && Array.isArray(followList) && followList.length > 0 && ctx.onDispositionNeeded) {
        close?.();
        ctx.onDispositionNeeded(followList as string[], async (dispositions) => {
          await submit(values, panel, close, { ...(top ?? {}), dispositions });
        });
        return;
      }
      report(panel, env.error?.message ?? 'That did not work.');
    } catch {
      report(panel, 'That did not work — a network error occurred.');
    } finally {
      btn.disabled = false;
    }
  }

  btn.addEventListener('click', () => {
    // CAOS-1904 — the one kind that does NOT go through `submit`, because its response is a session
    // rather than a result. `submit` reports: it reads `changedNothing`, draws `reports`, calls
    // `onSuccess` to redraw. None of that applies once the POST has succeeded — the person is now
    // somebody else, `onSuccess` would repaint a surface built for the previous one, and the door
    // leaves by a full document load so that nothing on screen half-belongs to each of them.
    //
    // It still runs through everything ABOVE this listener: `actionEnabled` has already disabled the
    // button and `enabledWhen.reason` has already captioned it, which is how AC2's three sentences
    // arrive without this kind knowing anything about them.
    if (action.kind === 'impersonate') {
      // WHO IS ACTED AS MUST COME FROM THE SCREEN, NOT FROM THE DOCUMENT. A review found this, and
      // it is the same hole the endpoint note below describes, one field over. `params` is
      // `Record<string, string>`, so an author — or a hand-built document that never passed a
      // deploy gate — can write `params: { principal: "<somebody else>" }` as a LITERAL, and
      // `substitute` returns an unrecognised value unchanged. The control on Dana's page would
      // then start a session as Morgan.
      //
      // NOTHING ESCALATES, which is what makes it worth refusing rather than shrugging at: the
      // kernel still checks the presser's own `users.impersonate` and its floors, and
      // `access-audit-envelope.ts` would correctly name whoever was really acted as. The audit
      // would be RIGHT and the operator deceived — a control captioned with Dana's name, gated on
      // Dana's `subject.status`, posting Morgan's id. AC1 asks for a session as THAT user.
      //
      // REFUSED RATHER THAN OVERRIDDEN, and that is the second attempt. Forcing
      // `principal: ctx.recordId` closes the hole and also breaks the affordance this kind is
      // tested to have: `paramsFrom` exists so a page whose ADDRESS is not the principal id can
      // still say which person it is about — CAOS-142's reason, and there is a case for it
      // directly below the one that found this. So the rule is about PROVENANCE, not about the
      // value: `principal` may be an address token, or read from the page's own payload through
      // `paramsFrom`; it may not be a constant written into the document.
      //
      // THE SCHEMA IS NOT WHERE THIS IS CLOSED, deliberately. A `params` constraint in
      // `metatypes.ts` would be a Zod `.refine`, and a Zod refinement does not reach the
      // generated Ajv validator the deploy actually runs — it would read as a gate and not be
      // one. The host is the layer BOTH doors pass through, so it is the layer where this holds
      // for an authored action and a forged one alike.
      const authoredPrincipal = action.params?.['principal'];
      if (
        authoredPrincipal !== undefined &&
        !ADDRESS_TOKENS.has(authoredPrincipal) &&
        action.paramsFrom?.['principal'] === undefined
      ) {
        report(
          undefined,
          'This control names a fixed person rather than the one on screen, so it has not been ' +
            'started. Report it: a Log in as control must take its target from the page.',
        );
        return;
      }
      askWhyThenStart({
        // Resolved by the SAME function every other kind uses, so `{recordId}` and `paramsFrom`
        // mean here exactly what they mean on a confirm — with the provenance of `principal`
        // checked above rather than its value rewritten here.
        body: resolveParams(action, ctx.recordId, ctx.rowId, ctx.groupHeading, ctx.payload),
        label: impersonationTargetLabel(action, ctx),
        // NO ENDPOINT IS PASSED, and that is deliberate after a review. This used to build
        // `/api/${action.verb.noun}/${action.verb.verb}` from the authored verb, which let an
        // author aim a control that reloads the document on success — with no success message,
        // no `reports` and no `onSuccess`, since this arm has none — at any write route. The
        // schema now pins the verb to `impersonation/start`, and the door posts to its own
        // constant, so the two agree without the host trusting the document: a hand-built action
        // that never passed a deploy gate cannot steer it either.
        ...(action.reasonLabel === undefined ? {} : { reasonLabel: action.reasonLabel }),
        ...(action.reasonHint === undefined ? {} : { reasonHint: action.reasonHint }),
        ...(action.offerWrite === undefined ? {} : { offerWrite: action.offerWrite }),
      });
      return;
    }
    if (action.kind === 'immediate') {
      void submit({});
      return;
    }
    if (action.kind === 'confirm') {
      // CAOS-1502 — an authored `preview` is what separates the two. Without one this is exactly
      // today's behaviour, unchanged, because most confirms genuinely are static questions and
      // replacing every one of them with a round trip would make the common case worse.
      if (action.preview) {
        void openPreviewingConfirm({
          action,
          doFetch,
          // The PREVIEW must resolve the same tokens as the write it previews. A preview built from a
      // different param map would describe a different call than the one the button makes.
      params: () => resolveParams(action, ctx.recordId, ctx.rowId, ctx.groupHeading, ctx.payload),
          submit: (extra, panel, close) => submit(extra, panel, close),
        });
        return;
      }
      if (!window.confirm(action.confirmText)) return;
      void submit({});
      return;
    }
    const { panel, close } = openDialog(action.label);
    const draw = (resolved: ResolvedFormFields): void => {
      if (resolved.failed) {
        // A form drawn from a read that failed is a form with fewer fields than the record has,
        // and nothing on it would say so. Refusing to draw it is the safe reading: the person
        // retries instead of saving a record they were shown only part of.
        showError(panel, 'This form could not load its fields. Close it and try again.');
        return;
      }
      if (resolved.empty) {
        showError(panel, nothingToDrawMessage(resolved.undrawable, 'edit'));
        return;
      }
      const form = el('form', 'roster-action-form');
      if (resolved.undrawable.length > 0) {
        const note = el('div', 'roster-action-note');
        note.textContent = notEditableHereNote(resolved.undrawable);
        form.append(note);
      }
      const readers = resolved.fields.map((f) => buildFieldInput(f, ctx.record, doFetch));
      for (const r of readers) form.append(r.row);
      const buttonRow = el('div', 'roster-action-buttons');
      const cancel = button('Cancel', 'default');
      cancel.addEventListener('click', close);
      const submitBtn = button(action.label, action.style ?? 'primary');
      submitBtn.type = 'submit';
      buttonRow.append(cancel, submitBtn);
      form.append(buttonRow);
      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const values: Record<string, unknown> = {};
        resolved.fields.forEach((f, i) => {
          const value = readers[i]!.read();
          // An empty optional number is absent rather than sent as nothing, so a route that
          // distinguishes "unset" from "zero" is told which one happened. `setOwn` because an
          // AUTHORED field key is not held to the body-key rule — only what a read answers is —
          // and `values['__proto__'] = x` would reassign this object rather than add to it.
          if (value !== undefined) setOwn(values, f.key, value);
        });
        void submit(values, panel, close);
      });
      panel.append(form);
    };

    // CAOS-1574 — a form with no `fieldsFrom` has nothing to wait for, and draws in the same turn
    // as the click, exactly as every form did before this ticket. Making it wait a microtask would
    // be a behaviour change nobody asked for and one a caller can SEE: a click that paints on the
    // next tick. A test that clicked and read the dialog immediately caught this, and it was
    // right to.
    if (action.fieldsFrom === undefined) {
      draw({ fields: [...(action.fields ?? [])], undrawable: [], failed: false, empty: false });
      return;
    }
    // With a read, the dialog opens on a placeholder and fills in. Opening only once the read
    // answers would leave the click with no visible effect for the length of a round trip, which
    // reads as a broken button.
    const pending = el('div', 'roster-action-pending');
    pending.textContent = 'Loading…';
    panel.append(pending);
    void resolveFormFields(action.fields, action.fieldsFrom, doFetch).then((resolved) => {
      pending.remove();
      draw(resolved);
    });
  });

  return btn;
}

export interface RunBulkActionContext {
  /** A confirm-kind action only (see the module doc — no batch verb exists; this fans ONE verb out
   *  over the selection, one call per id, so a form's extra fields have no single answer to reuse
   *  across every selected row). */
  readonly action: Extract<RosterAction, { readonly kind: 'confirm' }>;
  readonly recordId: string;
  /** Read at click time — the caller supplies the CURRENT selection (e.g. `caos-data-table`'s own
   *  `selectedIds` getter), never a snapshot taken when the button was built. */
  readonly getSelectedIds: () => readonly string[];
  /** CAOS-142 — the page payload `paramsFrom` reads from. A bulk action is drawn on a flat table,
   *  which has the payload, so a path here resolves exactly as it does for a row action. */
  readonly payload?: unknown;
  readonly fetch?: ActionFetch;
  /** Called once after the run if at least one call in the batch succeeded, so the caller can
   *  refresh its data (a partial success still needs the now-stale rows refetched). */
  readonly onSuccess: () => void;
}

/**
 * CAOS-557 — run a `bulkAction` (confirm once, then call its verb once per currently-selected row
 * id, substituting `{selectedId}`/`{rowId}` per call). Idempotent, independently-transactional
 * per-item verbs (e.g. `seats/allocate`, `seats/reclaim`) make this safe: one failing item never
 * rolls back the others, and repeating an id already succeeded elsewhere is a no-op, not an error.
 * Exported directly (not only via {@link renderRosterBulkActionButton}) so a caller can drive/assert
 * the run without a real click.
 *
 * CAOS-1715 — `unchanged` is the SUBSET of `succeeded` whose calls reported `noop`, and the two
 * are deliberately not merged.
 *
 * `succeeded` keeps the meaning this function's own contract gives it, stated two paragraphs up:
 * repeating an id that is already in the asked-for state is a no-op and not an error, so counting
 * a no-op as succeeded is the documented PREMISE of the bulk path, not a bug in it. It was
 * suggested that this counter be "fixed"; that would have broken the contract to correct a
 * sentence. What was wrong is only what the screen SAYS — see the alert in
 * {@link renderRosterBulkActionButton} — so the fix is to carry the extra fact and leave the
 * counter alone.
 */
export async function runRosterBulkAction(
  ctx: RunBulkActionContext,
): Promise<{ succeeded: string[]; unchanged: string[]; failed: { id: string; message: string }[] }> {
  const doFetch = ctx.fetch ?? defaultActionFetch;
  const ids = ctx.getSelectedIds();
  const succeeded: string[] = [];
  const unchanged: string[] = [];
  const failed: { id: string; message: string }[] = [];
  for (const id of ids) {
    const params = resolveParams(ctx.action, ctx.recordId, id, undefined, ctx.payload);
    try {
      // CAOS-1837 — `null`: a bulk press is one click and N responses, so N files would land on
      // the disk with nothing saying which row each came from. See {@link postAction}.
      const env = await postAction(doFetch, ctx.action, params, null);
      if (env.ok === true) {
        succeeded.push(id);
        if (changedNothing(env.data)) unchanged.push(id);
      } else failed.push({ id, message: env.error?.message ?? 'That did not work.' });
    } catch {
      failed.push({ id, message: 'A network error occurred.' });
    }
  }
  if (succeeded.length > 0) ctx.onSuccess();
  return { succeeded, unchanged, failed };
}

/**
 * CAOS-557 — build and wire ONE button for a table's `bulkAction`. Confirms once (using the
 * action's own `confirmText`), then calls {@link runRosterBulkAction} against whatever the caller's
 * `getSelectedIds` reports at that moment. Reports a partial failure by name (never silently drops
 * it) and always calls `onSuccess` when at least one item went through, so the caller's view
 * reflects the rows that actually changed even when others were refused.
 */
export function renderRosterBulkActionButton(ctx: RunBulkActionContext): HTMLButtonElement {
  const { action } = ctx;
  const btn = button(action.label, action.style ?? 'default');

  async function run(): Promise<void> {
    const ids = ctx.getSelectedIds();
    if (ids.length === 0) return;
    if (!window.confirm(action.confirmText)) return;
    btn.disabled = true;
    try {
      const { succeeded, unchanged, failed } = await runRosterBulkAction(ctx);
      /**
       * CAOS-1715 — two separate false claims lived in these four lines.
       *
       * 1. THE SENTENCE. `${succeeded.length} of ${ids.length} succeeded` is read as "that many
       *    changed", and `succeeded` legitimately counts no-ops (see {@link runRosterBulkAction}).
       *    A run that refused three items and no-opped two reported "2 of 5 succeeded" while
       *    nothing at all had moved — the same claim this ticket exists to remove, one level up
       *    from the single-item path and with the wider blast radius, because it speaks for a
       *    whole selection.
       *
       * 2. THE GATE. It required `failed.length > 0` before saying anything, so a run where
       *    EVERY item no-opped was completely silent: the person selected rows, confirmed, and
       *    the screen did not acknowledge it. Silence is not neutral here — the roster refreshes
       *    either way, so an unchanged run looked exactly like one that worked.
       *
       * Both now speak. The counts are stated as what they are — in the asked-for state, already
       * there, refused — rather than collapsed into one word that has to mean all three.
       *
       * STILL SILENT, DELIBERATELY: a run with no failures where SOME items changed and some were
       * already in that state. Something really happened, the roster refresh shows it, and the
       * incumbent behaviour for a clean run is to report through that refresh rather than a modal.
       * A modal on every partially-redundant selection would train people to dismiss it unread,
       * which costs more than the precision gains. The two branches below are the cases where the
       * screen would otherwise make or imply a claim that is false.
       */
      const changed = succeeded.length - unchanged.length;
      if (failed.length > 0) {
        const head =
          unchanged.length > 0
            ? `${changed} of ${ids.length} changed; ${unchanged.length} ${
                unchanged.length === 1 ? 'was' : 'were'
              } already in that state.`
            : `${changed} of ${ids.length} changed.`;
        window.alert(`${head}\n\nFailed:\n` + failed.map((f) => `${f.id} — ${f.message}`).join('\n'));
      } else if (changed === 0 && unchanged.length > 0) {
        window.alert(NOTHING_CHANGED);
      }
    } finally {
      btn.disabled = false;
    }
  }

  btn.addEventListener('click', () => void run());
  return btn;
}

/**
 * CAOS-337/CAOS-743 — the disposition follow-up: when a `confirm` action (today, only Setup Users'
 * Deactivate) is refused because one or more owned objects still need a decision, this renders one
 * row per object naming the THREE choices `data/user-deactivation.ts`'s gate accepts — "leave in
 * place", "transfer to role parent" (the gate's own default), or "transfer to…" (a named-principal
 * picker sourced from `principals/list`) — then resubmits with the assembled `dispositions` map.
 *
 * This is deliberately the one place in this module that is NOT fully generic: the three-kind
 * disposition contract (`kind` + optional `toOwnerId`) is a real, documented substrate shape
 * (`data/user-deactivation.ts`), not invented here — but nothing stops a FUTURE action from reusing
 * the same `followUpOnField` trigger with a different follow-up if one is ever needed; only this
 * renderer would need a sibling.
 */
export function renderDispositionFollowUp(
  objectKeys: readonly string[],
  resubmit: (dispositions: Record<string, unknown>) => Promise<void>,
  ctx: { readonly fetch?: ActionFetch; readonly onDone: () => void },
): void {
  const doFetch = ctx.fetch ?? defaultActionFetch;
  const { panel, close } = openDialog('Before deactivating — decide what happens to their records');
  const intro = el('p', 'roster-action-field-label');
  intro.textContent = `This person owns records in ${objectKeys.length} object${objectKeys.length === 1 ? '' : 's'}. Choose what happens to each before continuing.`;
  panel.append(intro);

  const form = el('form', 'roster-action-form');
  const kindSelects: { objectKey: string; kindSelect: HTMLSelectElement; targetSelect: HTMLSelectElement; targetRow: HTMLElement }[] = [];

  for (const objectKey of objectKeys) {
    const row = el('div', 'roster-action-field');
    const label = el('span', 'roster-action-field-label');
    label.textContent = objectKey;
    row.append(label);

    const kindSelect = el('select');
    for (const [value, text] of [
      ['transfer_to_role_parent', 'Transfer to role parent (default)'],
      ['leave_in_place', 'Leave in place'],
      ['transfer', 'Transfer to…'],
    ] as const) {
      const opt = el('option');
      opt.value = value;
      opt.textContent = text;
      kindSelect.append(opt);
    }
    row.append(kindSelect);

    const targetRow = el('label', 'roster-action-field');
    targetRow.style.display = 'none';
    const targetLabel = el('span', 'roster-action-field-label');
    targetLabel.textContent = 'Transfer to';
    const targetSelect = el('select');
    targetRow.append(targetLabel, targetSelect);
    void doFetch('/api/principals/list').then(async (res) => {
      const env = (await res.json()) as Envelope;
      if (env.ok !== true) return;
      const rows = readJsonPath(env.data, 'principals');
      if (!Array.isArray(rows)) return;
      for (const r of rows) {
        const value = String(readJsonPath(r, 'id') ?? '');
        // CAOS-746 — a person is named by their name when they have one, else by their login.
        const text = String(readJsonPath(r, 'displayName') ?? readJsonPath(r, 'identifier') ?? value);
        if (!value) continue;
        const opt = el('option');
        opt.value = value;
        opt.textContent = text;
        targetSelect.append(opt);
      }
    });

    kindSelect.addEventListener('change', () => {
      targetRow.style.display = kindSelect.value === 'transfer' ? '' : 'none';
    });

    kindSelects.push({ objectKey, kindSelect, targetSelect, targetRow });
    form.append(row, targetRow);
  }

  const buttonRow = el('div', 'roster-action-buttons');
  const cancel = button('Cancel', 'default');
  cancel.addEventListener('click', close);
  const submitBtn = button('Deactivate', 'danger');
  submitBtn.type = 'submit';
  buttonRow.append(cancel, submitBtn);
  form.append(buttonRow);

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    const dispositions: Record<string, unknown> = {};
    for (const { objectKey, kindSelect, targetSelect } of kindSelects) {
      // CAOS-1574 — `setOwn`, like every other write built from a name this code did not choose.
      // These keys come from the route's own refusal payload, and `dispositions['__proto__'] = {…}`
      // would reassign this object rather than add to it: the entry would vanish before the body
      // was serialised, the route would be refused again for the disposition it was just given,
      // and nothing on screen would say why. A component key cannot spell `__proto__` today, which
      // is the argument for leaving it — and is exactly the argument that stops being true the day
      // somebody widens the key pattern.
      const disposition =
        kindSelect.value === 'transfer' ? { kind: 'transfer', toOwnerId: targetSelect.value } : { kind: kindSelect.value };
      if (isBodyKey(objectKey)) setOwn(dispositions, objectKey, disposition);
    }
    submitBtn.disabled = true;
    void resubmit(dispositions).then(() => {
      close();
      ctx.onDone();
    });
  });

  panel.append(form);
}

/**
 * CAOS-1502 — the confirm dialog that finds out what it is about to do before it asks.
 *
 * A `confirmText` is fixed when the page is authored. That is the right shape for "Deactivate this
 * user?" and the wrong one for an uninstall, where the honest question names packages the author
 * could not have known. CAOS-978 built the answer route and nothing on the client called it.
 *
 * FOUR outcomes, all real, and only one of them is "here is what will happen". Authoring only the
 * happy one is how a dialog ends up claiming nothing else will be affected when it could not find
 * out — the failure this exists to prevent:
 *
 *   CONSEQUENCE   the preview succeeded and describes what goes with it. Confirm offered.
 *   REFUSAL       the preview returned the refusal the real verb is about to give. The kernel's
 *                 own message is shown and NO confirm is offered — except the one retry the
 *                 refusal itself says exists (see the cascade note below).
 *   UNSUPPORTED   `not_found.route`: a kernel predating CAOS-978. Degrades to `confirmText`, which
 *                 is the behaviour of every confirm before this ticket — never a blank dialog, and
 *                 never an accidental write. CAOS-978 chose a separate URL precisely so this case
 *                 is a clean answer rather than a real removal performed by mistake.
 *   UNKNOWN       the preview failed some other way. Confirm STAYS available, and the dialog says
 *                 plainly that the consequence could not be determined. Withholding the button
 *                 would strand someone on a transient error; claiming nothing else is affected
 *                 would be the lie.
 *
 * Every one of those has a way out, and the confirm is disabled for the whole round trip in both
 * directions — CAOS-881 names a spinner that never resolves as worse than no button at all.
 *
 * THE CASCADE re-runs the PREVIEW rather than submitting. The kernel's refusal states
 * `cascadeAvailable` and lists `dependentKeys` (CAOS-1516), and the retry it describes is a
 * different operation with a different, larger consequence. Submitting straight from the refusal
 * would ask someone to accept a list they were never shown; asking again with the flag set puts
 * them back in CONSEQUENCE, looking at the real one, with the flag carried into the write.
 */
interface PreviewingConfirmContext {
  readonly action: Extract<RosterAction, { readonly kind: 'confirm' }>;
  readonly doFetch: ActionFetch;
  readonly params: () => Record<string, string>;
  readonly submit: (extra: Record<string, unknown>, panel: HTMLElement, close: () => void) => Promise<void>;
}

/** Read the consequence list out of a preview payload, tolerating a route that names none. */
function readConsequences(data: unknown, consequenceAt: string | undefined): string[] {
  if (!consequenceAt) return [];
  const raw = readJsonPath(data, consequenceAt);
  if (!Array.isArray(raw)) return [];
  return raw.map((entry) => (typeof entry === 'string' ? entry : JSON.stringify(entry)));
}

export async function openPreviewingConfirm(ctx: PreviewingConfirmContext): Promise<void> {
  // The narrowing is handed on as a PARAMETER rather than kept as a narrowed const: the renderers
  // below are hoisted function declarations, and TypeScript will not carry a narrowing into one
  // because nothing stops a hoisted function being called before the guard that narrowed it.
  const preview = ctx.action.preview;
  if (!preview) return;
  await runPreviewingConfirm(ctx, preview);
}

async function runPreviewingConfirm(ctx: PreviewingConfirmContext, preview: RosterActionPreview): Promise<void> {
  const { action, doFetch } = ctx;
  const { panel, close } = openDialog(action.label);

  // Which ask() round trip is allowed to render. Cancelling, or choosing the cascade while the
  // first preview is still out, leaves an earlier fetch in flight; when it lands it must not paint
  // over what replaced it. Today the nodes it would touch are detached and the write is inert, so
  // this changes no behaviour you can see — it is here because that safety is an accident of the
  // dialog being thrown away whole, and the next person to reuse `body` across opens would lose it
  // silently. Cheaper to state the rule than to rediscover it.
  let generation = 0;
  const dialogClosed = () => {
    generation += 1;
    close();
  };

  const body = el('div', 'roster-action-preview');
  const buttonRow = el('div', 'roster-action-buttons');
  panel.append(body, buttonRow);

  const dismiss = button('Cancel', 'default');
  dismiss.addEventListener('click', dialogClosed);

  function confirmButton(cascadeChosen: boolean): HTMLButtonElement {
    const go = button(action.label, action.style ?? 'primary');
    go.addEventListener('click', () => {
      go.disabled = true;
      dismiss.disabled = true;
      const extra: Record<string, unknown> = {};
      // Sent ONLY when chosen — the flag is absent from the body otherwise, not present-and-false.
      if (cascadeChosen && action.cascade) extra[action.cascade.param] = 'true';
      // `dialogClosed`, not `close`: a submit that succeeds closes the dialog, and any preview
      // still in flight behind it must not repaint the panel on its way out.
      void ctx.submit(extra, panel, dialogClosed).finally(() => {
        go.disabled = false;
        dismiss.disabled = false;
      });
    });
    return go;
  }

  function renderConsequence(data: unknown, cascadeChosen: boolean): void {
    body.replaceChildren();
    const entries = readConsequences(data, preview.consequenceAt);
    if (entries.length === 0) {
      const p = el('p', 'roster-action-preview-empty');
      p.textContent = preview.emptyLabel ?? action.confirmText;
      body.append(p);
    } else {
      const lead = el('p', 'roster-action-preview-lead');
      lead.textContent = (preview.consequenceLabel ?? action.confirmText).replace('{count}', String(entries.length));
      const list = el('ul', 'roster-action-preview-list');
      for (const entry of entries) {
        const li = el('li');
        li.textContent = entry;
        list.append(li);
      }
      body.append(lead, list);
    }
    buttonRow.replaceChildren(dismiss, confirmButton(cascadeChosen));
  }

  function renderRefusal(error: NonNullable<Envelope['error']>, cascadeChosen: boolean): void {
    body.replaceChildren();
    const p = el('p', 'roster-action-preview-refusal');
    // The kernel's OWN message. A generic "that did not work" here is the thing CAOS-881 calls out.
    p.textContent = error.message ?? 'This cannot be done right now.';
    body.append(p);

    // `error.details`, not `error`. `ApiError(code, details)` (kernel/src/api/errors.ts) puts every
    // structured fact one level down, and the envelope carries it there — so a dialog reading the
    // facts off `error` itself finds nothing against a real kernel, however well it reads against a
    // flattened fixture. Caught by running this against a live org, not by a test.
    const details = (error['details'] ?? {}) as Record<string, unknown>;

    const dependents = details['dependentKeys'];
    if (Array.isArray(dependents) && dependents.length > 0) {
      const list = el('ul', 'roster-action-preview-list');
      for (const entry of dependents) {
        const li = el('li');
        li.textContent = String(entry);
        list.append(li);
      }
      body.append(list);
    }

    // No confirm. The one exception is the retry the refusal ITSELF says exists — and it re-asks
    // the preview rather than writing, so nobody accepts a larger consequence unseen.
    //
    // A kernel predating CAOS-1516 states neither `cascadeAvailable` nor `dependentKeys`, so it
    // gets the message and Cancel — which is right. The retry is offered because THIS refusal said
    // one exists, never because the surface assumes every refusal has one.
    buttonRow.replaceChildren(dismiss);
    if (details['cascadeAvailable'] === true && action.cascade && !cascadeChosen) {
      const again = button(action.cascade.label, action.style ?? 'primary');
      again.addEventListener('click', () => void ask(true));
      buttonRow.append(again);
    }
  }

  function renderStatic(cascadeChosen: boolean): void {
    body.replaceChildren();
    const p = el('p', 'roster-action-preview-static');
    p.textContent = action.confirmText;
    body.append(p);
    buttonRow.replaceChildren(dismiss, confirmButton(cascadeChosen));
  }

  function renderUnknown(cascadeChosen: boolean, message: string): void {
    body.replaceChildren();
    const p = el('p', 'roster-action-preview-unknown');
    // Says it could not find out, and still offers the button. The dialog that quietly implies
    // nothing else is affected is the failure this ticket exists to prevent.
    p.textContent = action.confirmText + ' ' + message;
    body.append(p);
    buttonRow.replaceChildren(dismiss, confirmButton(cascadeChosen));
  }

  /** Ask the preview route, then render whichever of the four outcomes came back. */
  async function ask(cascadeChosen: boolean): Promise<void> {
    const mine = ++generation;
    const stale = (): boolean => mine !== generation;
    body.replaceChildren();
    buttonRow.replaceChildren();
    // The explicit pending state. Not a spinner with no text: the dialog says what it is waiting
    // for, and Cancel stays live so this state is never a trap either.
    const pending = el('p', 'roster-action-preview-pending');
    pending.textContent = 'Checking what this affects…';
    body.append(pending);
    buttonRow.append(dismiss);

    const params = ctx.params();
    const previewBody: Record<string, unknown> = { ...params };
    if (cascadeChosen && action.cascade) previewBody[action.cascade.param] = 'true';

    let env: Envelope;
    try {
      const res = await doFetch('/api/' + preview.verb.noun + '/' + preview.verb.verb, { method: 'POST', body: previewBody });
      env = (await res.json()) as Envelope;
    } catch {
      if (stale()) return;
      renderUnknown(cascadeChosen, 'The consequence could not be determined — a network error occurred.');
      return;
    }
    if (stale()) return;

    if (env.ok === true) {
      renderConsequence(env.data, cascadeChosen);
      return;
    }

    const code = env.error?.code;
    if (code === 'not_found.route') {
      // A kernel predating CAOS-978. Today's static question, not a blank dialog.
      renderStatic(cascadeChosen);
      return;
    }
    if (typeof code === 'string' && code.length > 0) {
      renderRefusal(env.error ?? {}, cascadeChosen);
      return;
    }
    renderUnknown(cascadeChosen, 'The consequence could not be determined.');
  }

  await ask(false);
}

export interface RosterInlineFormContext {
  readonly action: Extract<RosterAction, { readonly kind: 'form' }>;
  /** The record the form's `defaultAt` paths read from. */
  readonly record: unknown;
  /** Substituted for every `"{recordId}"` token in `params`. */
  readonly recordId: string;
  readonly fetch?: ActionFetch;
  /** Called after a successful write so the caller can refresh its own data. */
  readonly onSuccess: () => void;
}

/**
 * CAOS-699 — a form-kind action drawn in place rather than behind a button: its fields and a submit
 * button labelled with the action, laid out as one row. Posts the fields plus `params` to the
 * action's verb; a refusal is shown under the form in the route's own words and the fields keep
 * what was typed, and a success calls `onSuccess` (the caller redraws, which clears the form).
 */
export function renderRosterInlineForm(ctx: RosterInlineFormContext): HTMLFormElement {
  const { action } = ctx;
  const doFetch = ctx.fetch ?? defaultActionFetch;
  const form = el('form', 'roster-inline-form');
  form.setAttribute('aria-label', action.label);
  const submitBtn = button(action.label, action.style ?? 'primary');
  submitBtn.type = 'submit';
  // CAOS-1574 — this form's fields can be an answer rather than a literal, and the function still
  // has to hand its caller an element now. So the controls arrive into a form that is already on
  // the page, with the submit disabled until they do: a form whose button works before its fields
  // exist would post an empty body on a fast click.
  let drawn: { fields: RosterActionField[]; readers: { row: HTMLElement; read: () => unknown }[] } = { fields: [], readers: [] };
  form.append(submitBtn);
  const draw = (resolved: ResolvedFormFields): void => {
    if (resolved.failed) {
      showError(form, 'This form could not load its fields. Reload the page and try again.');
      return;
    }
    if (resolved.empty) {
      showError(form, nothingToDrawMessage(resolved.undrawable, 'add'));
      return;
    }
    if (resolved.undrawable.length > 0) {
      const note = el('div', 'roster-action-note');
      note.textContent = notEditableHereNote(resolved.undrawable);
      form.prepend(note);
    }
    const readers = resolved.fields.map((f) => buildFieldInput(f, ctx.record, doFetch));
    for (const r of readers) submitBtn.before(r.row);
    drawn = { fields: resolved.fields, readers };
    submitBtn.disabled = false;
  };
  // CAOS-1574 — the same split as the dialog: with no read there is nothing to wait for, so the
  // form is complete when this function returns, which is what its caller has always been handed.
  if (action.fieldsFrom === undefined) {
    draw({ fields: [...(action.fields ?? [])], undrawable: [], failed: false, empty: false });
  } else {
    // The submit stays disabled until the controls arrive, so a fast click cannot post an empty
    // body from a form that has not finished drawing.
    submitBtn.disabled = true;
    void resolveFormFields(action.fields, action.fieldsFrom, doFetch).then(draw);
  }

  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (submitBtn.disabled) return;
    const values: Record<string, unknown> = {};
    drawn.fields.forEach((f, i) => {
      const value = drawn.readers[i]!.read();
      if (value !== undefined) setOwn(values, f.key, value);
    });
    let body: Record<string, unknown>;
    try {
      body = shapeSubmitBody(
        action.submit,
        resolveParams(action, ctx.recordId, undefined, undefined, ctx.record),
        values,
        ctx.record,
        ctx.recordId === undefined ? {} : { recordId: ctx.recordId },
      );
    } catch (error) {
      showError(form, error instanceof SubmitShapeError ? error.message : 'That did not work.');
      return;
    }
    submitBtn.disabled = true;
    void (async () => {
      try {
        // CAOS-1837 — `null`, on the same grounds the schema refuses `reports` on an `inlineForm`:
        // it is drawn by its own path and reports nothing, so it is not a shape that hands a file
        // back. An inline form that needs to produce one wants to be an action.
        const env = await postAction(doFetch, action, body, null);
        if (env.ok === true) {
          form.querySelector('.roster-action-error')?.remove();
          /**
           * CAOS-1715 — the fifth `env.ok === true` site, and the one CAOS-1712 deliberately left
           * alone pending a decision about where a message survives the redraw. Deciding it was
           * assigned to this ticket, so here is the decision and its reasoning.
           *
           * THE PROBLEM. This is the CAOS-1574 inline data-driven form. Its success path calls
           * `ctx.onSuccess()`, which redraws the surface this form is mounted in — so a notice
           * written INTO the form (the way `showError` writes failures into it) is destroyed
           * microseconds later by the very call that follows it. That is why it could not simply
           * copy the dialog's treatment, and why it was right to defer rather than guess.
           *
           * THE DECISION: `window.alert`, which is the channel this file ALREADY uses for a no-op
           * with nowhere to put a banner — see the dialog path, where an `immediate` or plain
           * `window.confirm` action submits with no panel and takes exactly this route. So this is
           * reading the incumbent rather than inventing a surface: one neutral channel, used in
           * the two places that have no surviving container, and modal so no redraw can wipe it.
           *
           * It is NOT `showError`: a no-op is an accepted write, and painting it in the failure
           * banner is the single claim this pair of tickets exists to stop making.
           *
           * WHAT WOULD BE BETTER, stated so the next person does not have to rediscover the
           * shape: a notice that lives OUTSIDE the redrawn region and survives it, the way the
           * roster detail page owns its own failure banner. That is a surface decision about
           * where such a region belongs, it is not free, and it is not a reason to keep shipping
           * either silence or a false success in the meantime.
           */
          if (changedNothing(env.data)) window.alert(NOTHING_CHANGED);
          ctx.onSuccess();
          return;
        }
        showError(form, env.error?.message ?? 'That did not work.');
      } catch {
        showError(form, 'That did not work — a network error occurred.');
      } finally {
        submitBtn.disabled = false;
      }
    })();
  });
  return form;
}
